import { Classes, Dialog, Spinner, TextArea } from "@blueprintjs/core"
import { PlanrPopover } from "modules/common/components/planr/popover/Popover"
import { observer } from "mobx-react"
import { FileLink } from "modules/common/components/files/FileLink"
import { Buttons } from "modules/common/components/form"
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import { DATE_FORMAT } from "modules/common/constants"
import { eat } from "modules/common/services/typescript"
import SignDialog from 'modules/main/components/crypto/CryptoDialog'
import { PendingSignatureItemType, SignatureDtoType } from 'modules/main/models/order-content-task-signatures'
import {
    ContentTaskFileType,
    OrderContentTasksListType
} from "modules/orders-manage/models/order-content-tasks"
import { Constants } from 'modules/root/models/constants'
import moment from "moment"
import React, { useState } from "react"
import { TUnit } from "./Block"
import styles from "./RowTask.module.scss"
import { OutsourcerDictionaryItemType } from 'modules/agents/outsourcers/models/outsourcer-dictionary'
import { AttachSignatureDialog } from 'modules/main/components/crypto/AttachSignatureDialog'
import VerifySignaturesDialog from 'modules/main/components/crypto/VerifySignaturesDialog'

export const getSignerType = (type: string): "worked" | "checked" | "control" | "legal" | "leader" | "gip" => {
    if (type === "worked" || type === "checked" || type === "control" || type === "leader" || type === 'legal' || type === 'gip') {
        return type
    }
    return "worked" // значение по умолчанию
}

// Функция для получения отображаемого названия роли
export const getSignerTypeDisplay = (type: string): string => {
    switch (type) {
        case "worked": return "Разработал"
        case "checked": return "Проверил"
        case "control": return "Нормоконтроль"
        case "leader": return "Начальник отдела"
        case "legal": return "Юр.лицо"
        case "gip": return "ГИП"
        default: return type
    }
}


interface RowTaskProps {
    contentTask: OrderContentTasksListType | undefined
    work: TUnit | null
    baseUrl: string
    highlighted?: string
    /** Файл из ссылки: блок файла ненадолго подсвечивается */
    highlightedFile?: string
    outsourcers: OutsourcerDictionaryItemType[]
    collapsed: boolean
    showDeleteTask: (guid: string, id: string) => void
    showAddTask: (guid: string) => void
    returnTask: (id: string) => void
    addAccept: (guid: string, value: boolean, id: string) => void
    addComplete: (guid: string, value: boolean, id: string) => void
    addConfirm: (guid: string, value: boolean, id: string) => void
    addCancell: (guid: string, value: boolean, id: string) => void
    showUploadFiles: (guid: string) => void
    showDevelopers: (guid: string, fileId: string) => void
    showEditTask: (
        descr: string,
        stopDate: string | null,
        id: string,
        file: any,
        guid: string,
        employerId: string | null
    ) => void
    downloadFiles: (guid: string) => Promise<any>
    downloadVersion: (guid: string, fileId: string, version: number) => Promise<any>
    deleteVersion: (guid: string, fileId: string, version: number) => void
    onToggleCollapse: () => void
    readOnly: boolean
    onNotifyError: (error: string) => void
    onNotifySuccess: (error: string) => void
    onSubmitSignature: (signature: PendingSignatureItemType, thumbprint: string, signatureData: string, rowVersion: number, signerId?: string | null) => Promise<void>
    onGetSignature: (id: string) => Promise<SignatureDtoType>
    onAttachSignature?: (signature: PendingSignatureItemType, signatureData: string, signerId?: string | null) => Promise<void>
    onClearSignatures?: (signatureId: string, fileId: string) => Promise<void>
    user: string
    orderId: string
    orderName: string
    gipId?: string
    saveFilePath?: (contentGuid: string, fileId: string, path: string) => Promise<void>
    people: SelectItem[]
}

interface RowTaskState {
    selectedDeleteVersion: ContentTaskFileType | null
    showDelete: boolean
    downloading: boolean
    versionDownloading: Record<string, boolean>
    copied: boolean
    showSignDialog: boolean
    selectedSignature: PendingSignatureItemType | null
    selectedSignerId: string | null
    selectedSignerName: string | null
    showAttachDialog: boolean
    attachSignature: PendingSignatureItemType | null
    selectedForVerify: any | null
    showVerifyDialog: boolean
    attachSignerName: string | null
    attachSignerId: string | null

}

class Row extends React.PureComponent<RowTaskProps, RowTaskState> {
    constructor(props: RowTaskProps) {
        super(props)

        this.state = {
            selectedDeleteVersion: null,
            downloading: false,
            versionDownloading: {},
            copied: false,
            showSignDialog: false,
            selectedSignature: null,
            showDelete: false,
            selectedSignerId: null, selectedSignerName: null,
            showAttachDialog: false,
            attachSignature: null,
            attachSignerName: null,
            attachSignerId: null,
            showVerifyDialog: false,
            selectedForVerify: null
        }
    }

    // Функция для приведения типа signerType


    closeSignDialog = () => {
        this.setState({
            showSignDialog: false,
            selectedSignature: null,
            selectedSignerId: null,
            selectedSignerName: null
        })
    }

    handleGetSignature = async (id: string): Promise<SignatureDtoType> => {
        // Используем переданный id, а не state
        const res = await this.props.onGetSignature(id)
        if (!res) {
            throw new Error('Не удалось получить данные подписи')
        }
        return res
    }

    handleGetVerifySignature = async (): Promise<SignatureDtoType> => {
        const res = await this.props.onGetSignature(this.state.selectedForVerify.signatureId)
        if (!res) {
            throw new Error('Не удалось получить данные подписи')
        }
        return res
    }

    // Функция для проверки, подписаны ли все файлы результата, у которых есть подписанты
    areAllResultFilesWithSigned = (): boolean => {
        const { contentTask } = this.props
        if (!contentTask) return false

        const resultFiles = contentTask.resultFiles || []

        // Фильтруем только те файлы, у которых есть подписанты
        const filesWithSignatures = resultFiles.filter((file) =>
            file.signatures && file.signatures.length > 0
        )

        // Если нет файлов с подписантами, возвращаем true (считаем что все ок)
        if (filesWithSignatures.length === 0) return true

        // Проверяем, что во всех файлах с подписантами все подписали
        return filesWithSignatures.every((file) =>
            file.signatures.every((s: any) => s.signed === true)
        )
    }
    handleSubmitSignature = async (
        signature: PendingSignatureItemType,
        thumbprint: string,
        signatureData: string,
        rowVersion: number
    ) => {
        // Используем selectedSignerId из state, если он есть
        const signerId = this.state.selectedSignerId
        return await this.props.onSubmitSignature(
            signature,
            thumbprint,
            signatureData,
            rowVersion,
            signerId
        )
    }

    handleVerifyClick = (signature: PendingSignatureItemType) => {
        this.setState({
            showVerifyDialog: true,
            selectedForVerify: signature,
        })
    }

    closeVerifyDialog = () => {
        this.setState({
            showVerifyDialog: false,
            selectedForVerify: null,
        })
    }

    closeAttachDialog = () => {
        this.setState({
            showAttachDialog: false,
            attachSignature: null,
            attachSignerName: null,
            attachSignerId: null,
        })
    }

    // Прикрепление файла подписи из окна подписания за другого человека
    handleAttachSignatureFromSign = async (signature: PendingSignatureItemType, signatureData: string) => {
        if (this.props.onAttachSignature) {
            await this.props.onAttachSignature(signature, signatureData, this.state.selectedSignerId)
        }
    }

    handleAttachSignature = async (signature: PendingSignatureItemType, signatureData: string) => {
        if (this.props.onAttachSignature) {
            await this.props.onAttachSignature(signature, signatureData, this.state.attachSignerId)
        }
    }

    render() {
        const {
            contentTask,
            work,
            baseUrl,
            returnTask,
            showAddTask,
            showEditTask,
            addAccept,
            showUploadFiles,
            showDevelopers,
            addComplete,
            addConfirm,
            addCancell,
            downloadVersion,
            readOnly,
            downloadFiles,
            deleteVersion,
            highlighted,
            highlightedFile,
            onNotifyError,
            onNotifySuccess,
            user,
            orderId,
            orderName, outsourcers, people
        } = this.props
        const { selectedDeleteVersion, showVerifyDialog, selectedForVerify } = this.state
        const canEdit = !readOnly
        const canUploadFiles = contentTask?.canUploadFiles || false
        const canChangeTask = contentTask?.canAddTasks || false
        const canDownloadStructure = contentTask?.canDownloadStructure || false
        const canDeleteVersion = contentTask?.canDeleteVersion || false
        // Сбросить подписи может тот, кто настраивает подписантов или удаляет версии, и ГИП проекта
        const canClearSignatures = canEdit && (canDeleteVersion || (!!this.props.gipId && user === this.props.gipId))

        const sourceFiles = contentTask?.sourceFiles || []
        const resultFiles = contentTask?.resultFiles || []
        const comments = contentTask?.comments?.slice().sort((a, b) => b.version - a.version) || []

        // let tasksCount = 0
        // if (contentTask) {
        //     tasksCount = contentTask.tasks.filter((t) => !t.confirmed).length
        // }
        let nowDate = new Date()
        nowDate.setHours(0, 0, 0, 0)

        // Группируем исходные файлы по имени
        const sourceFilesByName = sourceFiles.reduce((acc, file) => {
            if (!acc[file.name]) {
                acc[file.name] = []
            }
            acc[file.name].push(file)
            return acc
        }, {} as Record<string, any[]>)

        // Группируем файлы результата по имени
        const resultFilesByName = resultFiles.reduce((acc, file) => {
            if (!acc[file.name]) {
                acc[file.name] = []
            }
            acc[file.name].push(file)
            return acc
        }, {} as Record<string, any[]>)

        // Сортируем версии каждого файла по убыванию
        Object.keys(sourceFilesByName).forEach((key) => {
            sourceFilesByName[key].sort((a, b) => b.version - a.version)
        })

        Object.keys(resultFilesByName).forEach((key) => {
            resultFilesByName[key].sort((a, b) => b.version - a.version)
        })

        return work && (
            <div className={styles.rowTaskContainer}>
                <SignDialog
                    isOpen={this.state.showSignDialog}
                    selectedSignature={this.state.selectedSignature}
                    onClose={this.closeSignDialog}
                    onSubmitSignature={this.handleSubmitSignature}
                    onNotifyError={onNotifyError}
                    onGetSignature={this.handleGetSignature}
                    baseUrl={this.props.baseUrl}
                    selectedSignerName={this.state.selectedSignerName}
                    allowAttach={!!this.state.selectedSignerId && this.state.selectedSignerId !== user}
                    defaultMode={outsourcers.some((o) => o.id === this.state.selectedSignerId) ? 'attach' : 'cert'}
                    onAttachSignature={this.handleAttachSignatureFromSign}
                />
                <AttachSignatureDialog
                    isOpen={this.state.showAttachDialog}
                    selectedSignature={this.state.attachSignature}
                    selectedSignerName={this.state.attachSignerName}
                    onClose={this.closeAttachDialog}
                    onAttachSignature={this.handleAttachSignature}
                    onNotifyError={onNotifyError}
                    baseUrl={baseUrl}
                />
                <VerifySignaturesDialog
                    isOpen={showVerifyDialog}
                    signature={selectedForVerify}
                    onClose={this.closeVerifyDialog}
                    baseUrl={this.props.baseUrl}
                    onGetSignature={this.handleGetVerifySignature}  // Тот же метод, что и для SignDialog
                    onNotifyError={onNotifyError}
                    onNotifySuccess={onNotifySuccess}
                />
                {/* Фиксированная шапка */}
                <div className={styles.fixedHeader}>
                    <div className={styles.headerContent}>
                        <div className={styles.headerLeft}>
                            {/* <span className={styles.orderNumber}>{work.partNumber}</span> */}
                            <span className={styles.workName}>{work.name}</span>
                            {/* <span className={styles.description}>{work.description}</span> */}
                        </div>
                        <div className={styles.headerRight}>
                            {/* <span className={styles.progress}>{work.progress}%</span> */}
                            {canEdit && canChangeTask && (
                                <PlanrButton
                                    type="secondary"
                                    icon="general-plus"
                                    onClick={() => showAddTask(work.guid)}
                                    className={styles.addTaskBtn}
                                    size='small'
                                >
                                    Задание
                                </PlanrButton>
                            )}
                            {canUploadFiles && (
                                <PlanrButton
                                    type="lightblueish"
                                    icon="general-plus"
                                    size="small"
                                    title="Загрузить файлы"
                                    onClick={() => showUploadFiles(work.guid)}
                                >    Результат
                                </PlanrButton>
                            )}
                            {canDownloadStructure && (
                                <DownloadButton
                                    loading={this.state.downloading}
                                    onClick={async () => {
                                        try {
                                            this.setState({ downloading: true })
                                            await downloadFiles(contentTask?.guid || work.guid)
                                        } finally {
                                            this.setState({ downloading: false })
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Прокручиваемый контент */}
                <div className={styles.scrollableContent}>
                    {/* Блок заданий */}
                    {contentTask && contentTask.tasks.length > 0 && (
                        <div className={styles.tasksSection}>
                            <h3 className={styles.sectionTitle}>Задания</h3>
                            <div className={styles.leftTasks}>
                                {contentTask.tasks.map((task, index) => {
                                    const removed = task.toRemove
                                    const accepted = task.accepted
                                    const completed = task.completed
                                    const confirmed = task.confirmed
                                    const cancelled = task.cancelled
                                    const canChangeAccept = task.canChangeAcceptance
                                    const canChangeComplete = task.canChangeCompletion
                                    const canChangeConfirm = task.canChangeConfirmation
                                    const canChangeCancell = task.canChangeCancellation
                                    const canEditTask = task.canEditTask

                                    return (
                                        <div
                                            className={`${styles.rowTaskLeft} ${highlighted && highlighted === task.id && styles.highlighted}`}
                                            key={index}
                                            data-object-id={task.id}
                                            style={{
                                                backgroundColor: cancelled ? "#f6f6f6" : "",
                                            }}
                                        >
                                            <div>
                                                <div className={styles.rowTaskHeader}>
                                                    <div className={styles.rowTaskHeaderText}>
                                                        {task.description}
                                                    </div>
                                                    <div className={styles.rowTaskHeaderBtns}>
                                                        {!removed && canEdit && canChangeCancell && (
                                                            <PlanrButton
                                                                type={cancelled ? "lightgreenish" : "secondary"}
                                                                icon={cancelled ? "general-undo" : "general-cross-small"}
                                                                size="small"
                                                                title={cancelled ? "Восстановить задание" : "Аннулировать задание"}
                                                                onClick={
                                                                    canChangeCancell
                                                                        ? () => {
                                                                            const cancell = !cancelled
                                                                            addCancell(work.guid, cancell, task.id)
                                                                        }
                                                                        : eat
                                                                }
                                                            />
                                                        )}
                                                        {!cancelled && canEdit && canEditTask && (
                                                            <PlanrButton
                                                                type="lightblueish"
                                                                icon="general-edit"
                                                                size="small"
                                                                title="Редактировать задание"
                                                                onClick={() => {
                                                                    showEditTask(
                                                                        task.description,
                                                                        task.stopDate,
                                                                        task.id,
                                                                        task.taskFile,
                                                                        work.guid,
                                                                        task.implementer?.id || null
                                                                    )
                                                                }}
                                                            />
                                                        )}
                                                        {removed && canEdit && (
                                                            <PlanrButton
                                                                type="secondary"
                                                                size="small"
                                                                icon="general-undo"
                                                                title="Отменить удаление"
                                                                onClick={() => {
                                                                    returnTask(task.id)
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={styles.rowTaskLeftItemHeader}>
                                                    <div className={styles.employer}>Выдал</div>
                                                    <div className={styles.taskFileSmall}>Файл</div>
                                                    <div className={styles.date}>Дата от</div>
                                                    <div className={styles.implementer}>Исполнитель</div>
                                                    <div className={styles.stopDate}>Дата до</div>
                                                    <div className={styles.taskStatus}>Статус</div>
                                                </div>

                                                <div className={styles.rowTaskLeftItem}>
                                                    <div className={styles.employer}>{task.author}</div>
                                                    <div className={styles.taskFileSmall}>
                                                        {task.taskFile?.map((file) => (
                                                            <div style={{ display: "flex" }} key={file.id}>
                                                                <FileLink<any>
                                                                    key={file.id}
                                                                    baseUrl={baseUrl}
                                                                    file={{
                                                                        fileId: file.id,
                                                                        fileName: file.name,
                                                                    }}
                                                                    readOnly={true}
                                                                    onRemove={eat}
                                                                    littleIcon
                                                                    title={file.name}
                                                                />

                                                                {file.path && (
                                                                    <PlanrPopover
                                                                        placement="top"
                                                                        usePortal={true}
                                                                        interactionKind="hover"
                                                                        content={
                                                                            <div className={styles.pathWrapper}>
                                                                                <div>
                                                                                    <div>Путь к файлу</div>
                                                                                    <div className={styles.pathText}>
                                                                                        {file.path}
                                                                                    </div>
                                                                                </div>
                                                                                {!this.state.copied ? (
                                                                                    <PlanrButton
                                                                                        type="secondary"
                                                                                        icon="general-copy"
                                                                                        title="Копировать"
                                                                                        size="small"
                                                                                        onClick={async () => {
                                                                                            try {
                                                                                                await navigator.clipboard.writeText(file.path || "")
                                                                                            } catch (e) {
                                                                                            } finally {
                                                                                                this.setState({ copied: true })
                                                                                                setTimeout(() => {
                                                                                                    this.setState({ copied: false })
                                                                                                }, 1000)
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    <PlanrButton
                                                                                        type="greenish"
                                                                                        icon="general-check"
                                                                                        size="small"
                                                                                    />
                                                                                )}
                                                                            </div>
                                                                        }
                                                                    >
                                                                        <span className={styles.withDataCell}>
                                                                            <GeneralIcon
                                                                                type="general-attach"
                                                                                style={{
                                                                                    cursor: "pointer",
                                                                                    color: "#1DD278",
                                                                                    marginLeft: "10px",
                                                                                }}
                                                                            />
                                                                        </span>
                                                                    </PlanrPopover>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                    <div className={styles.date}>
                                                        {task.date}
                                                        <br />
                                                        <span className={styles.time}>{task.time}</span>
                                                    </div>
                                                    <div className={styles.implementer}>
                                                        {task.implementer ? (
                                                            task.implementer.name
                                                        ) : (
                                                            <span className={styles.noDataCell}>Нет</span>
                                                        )}
                                                    </div>
                                                    <div className={styles.stopDate}>
                                                        {task.stopDate ? (
                                                            <span
                                                                style={{
                                                                    color:
                                                                        moment(task.stopDate, DATE_FORMAT).toDate() < nowDate
                                                                            ? "#e31818"
                                                                            : "",
                                                                }}
                                                            >
                                                                {task.stopDate}
                                                            </span>
                                                        ) : (
                                                            <span className={styles.noDataCell}>Нет</span>
                                                        )}
                                                    </div>

                                                    <div className={styles.taskStatus}>
                                                        <div
                                                            className={`${styles.taskStatusRow} ${!canChangeAccept ? styles.cantChangeState : ""}`}
                                                            title="Доступ: Ответственный"
                                                        >
                                                            <div className={styles.taskStatusRowLabel}>Принял</div>
                                                            <div className={styles.taskStatusRowBtn}>
                                                                <GeneralIcon
                                                                    type={accepted ? "general-checkbox" : "general-zero-checkbox"}
                                                                    style={{ cursor: "pointer" }}
                                                                    onClick={
                                                                        canChangeAccept
                                                                            ? () => {
                                                                                const accept = !accepted
                                                                                addAccept(work.guid, accept, task.id)
                                                                            }
                                                                            : eat
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                        <div
                                                            className={`${styles.taskStatusRow} ${!canChangeComplete ? styles.cantChangeState : ""}`}
                                                            title="Доступ: Ответственный, Исполнитель"
                                                        >
                                                            <div className={styles.taskStatusRowLabel}>Выполнил</div>
                                                            <div className={styles.taskStatusRowBtn}>
                                                                <GeneralIcon
                                                                    type={completed ? "general-checkbox" : "general-zero-checkbox"}
                                                                    style={{ cursor: "pointer" }}
                                                                    onClick={
                                                                        canChangeComplete
                                                                            ? () => {
                                                                                const complete = !completed
                                                                                addComplete(work.guid, complete, task.id)
                                                                            }
                                                                            : eat
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                        <div
                                                            className={`${styles.taskStatusRow} ${!canChangeConfirm ? styles.cantChangeState : ""}`}
                                                            title="Доступ: Выдал, Ответственный"
                                                        >
                                                            <div className={styles.taskStatusRowLabel}>Подтвердил</div>
                                                            <div className={styles.taskStatusRowBtn}>
                                                                <GeneralIcon
                                                                    type={confirmed ? "general-checkbox" : "general-zero-checkbox"}
                                                                    style={{ cursor: "pointer" }}
                                                                    onClick={
                                                                        canChangeConfirm
                                                                            ? () => {
                                                                                const confirm = !confirmed
                                                                                addConfirm(work.guid, confirm, task.id)
                                                                            }
                                                                            : eat
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}

                    {/* Блок результата выполнения */}
                    <div className={styles.resultSection}>
                        <h3 className={styles.sectionTitle}>Результат выполнения</h3>

                        {contentTask && (sourceFiles.length > 0 || resultFiles.length > 0) ? (
                            <div className={styles.rowTaskRight}>
                                {/* Заголовки таблицы файлов */}
                                <div className={styles.filesTableHeader}>
                                    <div className={styles.fileTypeCol}>Тип</div>
                                    <div className={styles.fileNameCol}>Файл</div>
                                    <div className={styles.fileVersionCol} style={{ color: "#00273d", cursor: "default" }}>Версия</div>
                                    <div className={styles.fileDateCol}>Дата загрузки</div>
                                </div>

                                <Dialog
                                    title={`Удаление версии`}
                                    isOpen={this.state.showDelete}
                                    onClose={() => this.setState({ showDelete: false, selectedDeleteVersion: null })}
                                    backdropClassName="standard"
                                    className={`${styles.FilesDialog} figma-dialog`}
                                >
                                    <div className={`${Classes.DIALOG_BODY} `}>
                                        <div className={styles.showDialogTask}>
                                            {selectedDeleteVersion && `Вы действительно хотите удалить файл "${selectedDeleteVersion.name}" (версия ${selectedDeleteVersion.version})?`}
                                        </div>
                                    </div>
                                    <div className={Classes.DIALOG_FOOTER}>
                                        <Buttons
                                            left={
                                                <div style={{ display: "flex" }}>
                                                    <PlanrButton
                                                        size="small"
                                                        type="danger"
                                                        onClick={() => {
                                                            if (selectedDeleteVersion) {
                                                                deleteVersion(contentTask.guid, selectedDeleteVersion.id, selectedDeleteVersion.version)
                                                                this.setState({ showDelete: false, selectedDeleteVersion: null })
                                                            }
                                                        }}
                                                    >
                                                        Удалить
                                                    </PlanrButton>
                                                    <PlanrButton
                                                        size="small"
                                                        type="graybtn"
                                                        onClick={() => this.setState({ showDelete: false, selectedDeleteVersion: null })}
                                                    >
                                                        Отмена
                                                    </PlanrButton>
                                                </div>
                                            }
                                        />
                                    </div>
                                </Dialog>

                                {/* Сначала отображаем исходные файлы */}
                                {Object.entries(sourceFilesByName).map(([fileName, fileVersions]) => {
                                    const latestVersion = fileVersions[0]
                                    const uploaded = people.find((user) => user.id === latestVersion.employerId)
                                    return (
                                        <div key={`source-${fileName}`} className={styles.fileVersionGroup}>
                                            <div className={styles.fileRow}>
                                                <div className={styles.fileTypeCol}>
                                                    <span className={styles.sourceBadge}>
                                                        Исходный
                                                    </span>
                                                </div>
                                                <div className={styles.fileNameCol}>
                                                    <FileLink
                                                        baseUrl={baseUrl}
                                                        file={{
                                                            fileId: latestVersion.id,
                                                            fileName: latestVersion.name,
                                                        }}
                                                        readOnly={true}
                                                        onRemove={eat}
                                                        style={{
                                                            whiteSpace: "normal",
                                                            fontSize: " 10px",
                                                            height: "auto",
                                                            padding: "8px 12px",
                                                            borderColor: "#ea561e",
                                                            color: "#ea561e"
                                                        }}
                                                    />
                                                </div>
                                                <div className={styles.fileVersionCol}>
                                                    <PlanrPopover
                                                        placement="right"
                                                        usePortal={true}
                                                        interactionKind="hover"
                                                        boundary={document.body}
                                                        content={
                                                            <VersionDetails
                                                                fileVersions={fileVersions}
                                                                baseUrl={baseUrl}
                                                                downloadVersion={downloadVersion}
                                                                removeVersion={deleteVersion}
                                                                canDownload={canDownloadStructure}
                                                                canDelete={canDeleteVersion}
                                                                onNotifySuccess={onNotifySuccess}
                                                                taskGuid={contentTask.guid}
                                                            />
                                                        }
                                                    >
                                                        <span className={styles.versionLink}>
                                                            Версия_{latestVersion.version}
                                                            {fileVersions.length > 1 && (
                                                                <GeneralIcon
                                                                    type="general-arrow-down"
                                                                    style={{ marginLeft: '4px', fontSize: '10px' }}
                                                                />
                                                            )}
                                                        </span>
                                                    </PlanrPopover>
                                                </div>
                                                <div className={styles.fileDateCol}>
                                                    {latestVersion.created}
                                                    <br />
                                                    <span className={styles.time}>{latestVersion.time}</span>
                                                </div>

                                                <div className={styles.fileActionsCol}>
                                                    {(canDeleteVersion || latestVersion.canDeleteVersion) && (
                                                        <PlanrButton
                                                            type="lightdanger"
                                                            size="small"
                                                            icon="general-trash"
                                                            title="Удалить версию"
                                                            onClick={() => this.setState({ showDelete: true, selectedDeleteVersion: latestVersion })}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            {uploaded && <div className={styles.uploaded}>{`Загрузил:  ${uploaded.label}`}</div>}
                                            <div className={styles.filePathContainer}>
                                                <FilePathEditor
                                                    filePath={latestVersion.path}
                                                    fileId={latestVersion.id}
                                                    onSave={async (fileId, path) => {
                                                        if (this.props.saveFilePath) {
                                                            await this.props.saveFilePath(contentTask.guid, fileId, path)
                                                        }
                                                    }}
                                                    readOnly={!canDeleteVersion && !latestVersion.canDeleteVersion}
                                                    onCopy={() => {
                                                        this.props.onNotifySuccess?.('Путь скопирован в буфер обмена')
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )
                                })}

                                {/* Затем отображаем файлы результата */}
                                {Object.entries(resultFilesByName).map(([fileName, fileVersions]) => {
                                    const latestVersion = fileVersions[0]
                                    const uploaded = people.find((user) => user.id === latestVersion.employerId)

                                    // Находим все подписи текущего пользователя для этого файла
                                    const userSignatures = latestVersion.signatures?.filter((s: any) => s.employerId === user) || []
                                    const signature = latestVersion.signatures.length > 0 ? latestVersion.signatures[0] : null
                                    // Проверяем, есть ли неподписанные роли у пользователя
                                    const hasUnsignedRoles = userSignatures.some((s: any) => !s.signed)

                                    // Проверяем, подписали ли все вообще
                                    const allSigned = latestVersion.signatures?.length > 0 &&
                                        latestVersion.signatures.every((s: any) => s.signed)

                                    // ссылка может вести на любую версию файла — подсвечиваем весь блок
                                    const linked = !!highlightedFile && fileVersions.some((v) => v.id === highlightedFile)

                                    return (
                                        <div key={`result-${fileName}`}
                                            className={`${styles.fileVersionGroup} ${linked ? styles.linkedFile : ""}`}
                                            data-object-id={linked ? highlightedFile : latestVersion.id}
                                            style={allSigned ? { borderColor: "#1dd278" } : {}}>

                                            <div className={styles.fileRowWrapper}>
                                                <div className={styles.fileRow}>
                                                    <div className={styles.fileTypeCol}>
                                                        <span className={styles.resultBadge}>
                                                            Результат
                                                        </span>
                                                    </div>
                                                    <div className={styles.fileNameCol}>
                                                        <FileLink
                                                            baseUrl={baseUrl}
                                                            file={{
                                                                fileId: latestVersion.id,
                                                                fileName: latestVersion.name,
                                                            }}
                                                            readOnly={true}
                                                            onRemove={eat}
                                                            style={{
                                                                whiteSpace: "normal",
                                                                fontSize: " 10px",
                                                                height: "auto",
                                                                padding: "8px 12px",
                                                                borderColor: "#ea561e",
                                                                color: "#ea561e"
                                                            }}
                                                        />
                                                    </div>
                                                    <div className={styles.fileVersionCol}>
                                                        <PlanrPopover
                                                            placement="right"
                                                            usePortal={true}
                                                            interactionKind="hover"
                                                            content={
                                                                <VersionDetails
                                                                    fileVersions={fileVersions}
                                                                    baseUrl={baseUrl}
                                                                    downloadVersion={downloadVersion}
                                                                    removeVersion={deleteVersion}
                                                                    canDownload={canDownloadStructure}
                                                                    canDelete={canDeleteVersion}
                                                                    taskGuid={contentTask.guid}
                                                                    onNotifySuccess={onNotifySuccess}
                                                                />
                                                            }
                                                            boundary={document.body}
                                                        >
                                                            <span className={styles.versionLink}>
                                                                Версия_{latestVersion.version}
                                                                {fileVersions.length > 1 && (
                                                                    <GeneralIcon
                                                                        type="general-arrow-down"
                                                                        style={{ marginLeft: '4px', fontSize: '10px' }}
                                                                    />
                                                                )}
                                                            </span>
                                                        </PlanrPopover>
                                                    </div>
                                                    <div className={styles.fileDateCol}>
                                                        {latestVersion.created}
                                                        <br />
                                                        <span className={styles.time}>{latestVersion.time}</span>
                                                    </div>

                                                    <div className={styles.fileResultActionsCol}>
                                                        {/* Кнопка "Подписи" для настройки подписантов */}
                                                        <div style={{ display: "flex", width: "100%", justifyContent: "flex-end" }}>
                                                            {canEdit && (
                                                                <PlanrButton
                                                                    type="lightblueish"
                                                                    size="small"
                                                                    title="Настроить подписи"
                                                                    icon='general-add-user'
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        e.preventDefault()

                                                                        if (!latestVersion || !work?.guid || !latestVersion.id) {
                                                                            return
                                                                        }

                                                                        showDevelopers(work.guid, latestVersion.id)
                                                                    }}
                                                                />
                                                            )}

                                                            {(canDownloadStructure || latestVersion.canDeleteVersion) && (
                                                                <DownloadButton
                                                                    title='Скачать файл результата с файлом ЭЦП'
                                                                    loading={this.state.versionDownloading[latestVersion.id] || false}
                                                                    onClick={async () => {
                                                                        try {
                                                                            this.setState((prev) => ({
                                                                                versionDownloading: {
                                                                                    ...prev.versionDownloading,
                                                                                    [latestVersion.id]: true
                                                                                }
                                                                            }))
                                                                            await downloadVersion(contentTask.guid, latestVersion.id, latestVersion.version)
                                                                        } finally {
                                                                            this.setState((prev) => ({
                                                                                versionDownloading: {
                                                                                    ...prev.versionDownloading,
                                                                                    [latestVersion.id]: false
                                                                                }
                                                                            }))
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                            {(canDeleteVersion || latestVersion.canDeleteVersion) && (
                                                                <PlanrButton
                                                                    type="lightdanger"
                                                                    size="small"
                                                                    icon="general-trash"
                                                                    title="Удалить версию"
                                                                    onClick={() => this.setState({ showDelete: true, selectedDeleteVersion: latestVersion })}
                                                                />
                                                            )}
                                                        </div>

                                                        {/* Единая кнопка "Подписать" - показываем если у пользователя есть неподписанные роли */}
                                                        {!allSigned && hasUnsignedRoles && (
                                                            <PlanrButton
                                                                type='greenish'
                                                                size="small"
                                                                style={{ width: '190px', marginLeft: "0px", marginTop: "8px" }}
                                                                onClick={() => {
                                                                    // Находим первую неподписанную роль пользователя
                                                                    const firstUnsigned = userSignatures.find((s: any) => !s.signed)

                                                                    if (firstUnsigned) {
                                                                        const signatureData: PendingSignatureItemType = {
                                                                            signatureId: firstUnsigned.signatureId,
                                                                            orderId: orderId,
                                                                            orderNumber: work.partNumber,
                                                                            orderName: orderName,
                                                                            contentGuid: work.guid,
                                                                            contentName: work.name,
                                                                            version: latestVersion.version,
                                                                            signerType: getSignerType(firstUnsigned.signerType),
                                                                            signerTypeDisplay: getSignerTypeDisplay(firstUnsigned.signerType),
                                                                            fileName: latestVersion.name,
                                                                            fileId: latestVersion.id,
                                                                            createdAt: latestVersion.date || "",
                                                                            date: latestVersion.date || "",
                                                                            existingSignature: "",
                                                                            signatureSource: "task"
                                                                        }

                                                                        this.setState({
                                                                            showSignDialog: true,
                                                                            selectedSignature: signatureData
                                                                        })
                                                                    }
                                                                }}
                                                            >
                                                                Подписать
                                                            </PlanrButton>
                                                        )}

                                                        {/* Если все подписали - показываем статус */}
                                                        {allSigned && (
                                                            <span className={styles.allSignedLabel}>
                                                                <GeneralIcon type="general-check" style={{ marginRight: '4px', color: '#1DD278' }} />
                                                                Подписано
                                                            </span>
                                                        )}

                                                        {latestVersion.signatures.some((s: any) => s.signed) && (
                                                            <div className={styles.buttons}>
                                                                <PlanrButton
                                                                    type='secondary'
                                                                    size="small"
                                                                    style={{ width: '190px', marginLeft: "0px", marginTop: "8px" }}
                                                                    onClick={() => { this.handleVerifyClick(signature) }}
                                                                >
                                                                    Проверить ЭЦП
                                                                </PlanrButton>
                                                                {canClearSignatures && signature && this.props.onClearSignatures && (
                                                                    <RemoveConfirmation<ContentTaskFileType>
                                                                        what={(file) => `все подписи файла "${file.name}" (версия ${file.version})? Подписантам придётся подписать его заново`}
                                                                        actionName="сбросить"
                                                                        onConfirmed={(file) => {
                                                                            const fileSignature = file.signatures[0]
                                                                            if (fileSignature && this.props.onClearSignatures) {
                                                                                this.props.onClearSignatures(fileSignature.signatureId, file.id)
                                                                            }
                                                                        }}
                                                                    >
                                                                        {({ confirmRemoving }) => (
                                                                            <PlanrButton
                                                                                type='danger'
                                                                                size="small"
                                                                                title="Сбросить все подписи файла — подписанты подпишут заново"
                                                                                style={{ width: '190px', height: '24px', minHeight: '24px', fontSize: '12px', border: '1px dashed #e31818', background: 'transparent', color: '#e31818', marginLeft: "0px", marginTop: "8px" }}
                                                                                onClick={() => confirmRemoving(latestVersion)}
                                                                            >
                                                                                Очистить подписи
                                                                            </PlanrButton>
                                                                        )}
                                                                    </RemoveConfirmation>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {uploaded && <div className={styles.uploaded}>{`Загрузил:   ${uploaded.label}`}</div>}

                                                <div className={styles.filePathContainer}>
                                                    <FilePathEditor
                                                        filePath={latestVersion.path}
                                                        fileId={latestVersion.id}
                                                        onSave={async (fileId, path) => {
                                                            if (this.props.saveFilePath) {
                                                                await this.props.saveFilePath(contentTask.guid, fileId, path)
                                                            }
                                                        }}
                                                        readOnly={!canDeleteVersion && !latestVersion.canDeleteVersion}
                                                        onCopy={() => {
                                                            this.props.onNotifySuccess?.('Путь скопирован в буфер обмена')
                                                        }}
                                                    />
                                                </div>

                                            </div>

                                            {/* Отображаем подписи для этой версии файла результата */}
                                            {latestVersion.signatures?.length > 0 && (
                                                <div className={styles.signaturesContainer}>
                                                    <div className={styles.signaturesThreeColumns}>
                                                        {/* Столбец "Разработал" */}
                                                        <div className={styles.signatureColumn}>
                                                            <div className={styles.columnHeader}>Разработал</div>
                                                            {latestVersion.signatures
                                                                .filter((s: any) => s.signerType === 'worked')
                                                                .map((s: any) => (
                                                                    <div key={`worked-${s.employerId}`} className={styles.signatureRow}>
                                                                        <span className={styles.signerName} title={s.employerName}>
                                                                            {s.employerName}
                                                                        </span>
                                                                        <SignatureButton
                                                                            signature={s}
                                                                            latestVersion={latestVersion}
                                                                            work={work}
                                                                            orderId={orderId}
                                                                            orderName={orderName}
                                                                            canEdit={canEdit}
                                                                            user={user}
                                                                            isOutsourcer={!!outsourcers.find((o) => o.id === s.employerId)}
                                                                            gipId={this.props.gipId}
                                                                            onOpenSignDialog={(signatureData, signerId, signerName) => {
                                                                                this.setState({
                                                                                    showSignDialog: true,
                                                                                    selectedSignature: signatureData,
                                                                                    selectedSignerId: signerId,
                                                                                    selectedSignerName: signerName
                                                                                })
                                                                            }}
                                                                            onOpenAttachDialog={(signatureData, signerId, signerName) => {
                                                                                this.setState({
                                                                                    showAttachDialog: true,
                                                                                    attachSignature: signatureData,
                                                                                    attachSignerId: signerId,
                                                                                    attachSignerName: signerName
                                                                                })
                                                                            }}
                                                                            getSignerType={getSignerType}
                                                                            getSignerTypeDisplay={getSignerTypeDisplay}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            {latestVersion.signatures.filter((s: any) => s.signerType === 'worked').length === 0 && (
                                                                <div className={styles.emptyColumn}>—</div>
                                                            )}
                                                        </div>

                                                        {/* Столбец "Проверил" */}
                                                        <div className={styles.signatureColumn}>
                                                            <div className={styles.columnHeader}>Проверил</div>
                                                            {latestVersion.signatures
                                                                .filter((s: any) => s.signerType === 'checked')
                                                                .map((s: any) => (
                                                                    <div key={`checked-${s.employerId}`} className={styles.signatureRow}>
                                                                        <span className={styles.signerName} title={s.employerName}>
                                                                            {s.employerName}
                                                                        </span>
                                                                        <SignatureButton
                                                                            signature={s}
                                                                            latestVersion={latestVersion}
                                                                            work={work}
                                                                            orderId={orderId}
                                                                            orderName={orderName}
                                                                            canEdit={canEdit}
                                                                            user={user}
                                                                            isOutsourcer={!!outsourcers.find((o) => o.id === s.employerId)}
                                                                            gipId={this.props.gipId}
                                                                            onOpenSignDialog={(signatureData, signerId, signerName) => {
                                                                                this.setState({
                                                                                    showSignDialog: true,
                                                                                    selectedSignature: signatureData,
                                                                                    selectedSignerId: signerId,
                                                                                    selectedSignerName: signerName
                                                                                })
                                                                            }} onOpenAttachDialog={(signatureData, signerId, signerName) => {
                                                                                this.setState({
                                                                                    showAttachDialog: true,
                                                                                    attachSignature: signatureData,
                                                                                    attachSignerId: signerId,
                                                                                    attachSignerName: signerName
                                                                                })
                                                                            }}
                                                                            getSignerType={getSignerType}
                                                                            getSignerTypeDisplay={getSignerTypeDisplay}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            {latestVersion.signatures.filter((s: any) => s.signerType === 'checked').length === 0 && (
                                                                <div className={styles.emptyColumn}>—</div>
                                                            )}
                                                            {/* Юр.лицо (отображается под нормоконтролем, если есть) */}
                                                            {latestVersion.signatures.some((s: any) => s.signerType === 'leader') && (
                                                                <div className={styles.legalSection}>
                                                                    <div className={styles.legalHeader}>Начальник отдела</div>
                                                                    {latestVersion.signatures
                                                                        .filter((s: any) => s.signerType === 'leader')
                                                                        .map((s: any) => (
                                                                            <div key={`legal-${s.employerId}`} className={styles.signatureRow}>
                                                                                <span className={styles.signerName} title={s.employerName}>
                                                                                    {s.employerName}
                                                                                </span>
                                                                                <SignatureButton
                                                                                    signature={s}
                                                                                    latestVersion={latestVersion}
                                                                                    work={work}
                                                                                    orderId={orderId}
                                                                                    orderName={orderName}
                                                                                    canEdit={canEdit}
                                                                                    isOutsourcer={!!outsourcers.find((o) => o.id === s.employerId)}
                                                                                    user={user}
                                                                                    gipId={this.props.gipId}
                                                                                    onOpenSignDialog={(signatureData, signerId, signerName) => {
                                                                                        this.setState({
                                                                                            showSignDialog: true,
                                                                                            selectedSignature: signatureData,
                                                                                            selectedSignerId: signerId,
                                                                                            selectedSignerName: signerName
                                                                                        })
                                                                                    }} onOpenAttachDialog={(signatureData, signerId, signerName) => {
                                                                                        this.setState({
                                                                                            showAttachDialog: true,
                                                                                            attachSignature: signatureData,
                                                                                            attachSignerId: signerId,
                                                                                            attachSignerName: signerName
                                                                                        })
                                                                                    }}
                                                                                    getSignerType={getSignerType}
                                                                                    getSignerTypeDisplay={getSignerTypeDisplay}
                                                                                />
                                                                            </div>
                                                                        ))}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Столбец "Нормоконтроль" + Юр.лицо */}
                                                        <div className={styles.signatureColumn}>
                                                            <div className={styles.columnHeader}>Нормоконтроль</div>
                                                            {latestVersion.signatures
                                                                .filter((s: any) => s.signerType === 'control')
                                                                .map((s: any) => (
                                                                    <div key={`control-${s.employerId}`} className={styles.signatureRow}>
                                                                        <span className={styles.signerName} title={s.employerName}>
                                                                            {s.employerName}
                                                                        </span>
                                                                        <SignatureButton
                                                                            signature={s}
                                                                            latestVersion={latestVersion}
                                                                            work={work}
                                                                            orderId={orderId}
                                                                            orderName={orderName}
                                                                            canEdit={canEdit}
                                                                            isOutsourcer={!!outsourcers.find((o) => o.id === s.employerId)}
                                                                            user={user}
                                                                            gipId={this.props.gipId}
                                                                            onOpenSignDialog={(signatureData, signerId, signerName) => {
                                                                                this.setState({
                                                                                    showSignDialog: true,
                                                                                    selectedSignature: signatureData,
                                                                                    selectedSignerId: signerId,
                                                                                    selectedSignerName: signerName
                                                                                })
                                                                            }} onOpenAttachDialog={(signatureData, signerId, signerName) => {
                                                                                this.setState({
                                                                                    showAttachDialog: true,
                                                                                    attachSignature: signatureData,
                                                                                    attachSignerId: signerId,
                                                                                    attachSignerName: signerName
                                                                                })
                                                                            }}
                                                                            getSignerType={getSignerType}
                                                                            getSignerTypeDisplay={getSignerTypeDisplay}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            {latestVersion.signatures.filter((s: any) => s.signerType === 'control').length === 0 && (
                                                                <div className={styles.emptyColumn}>—</div>
                                                            )}
                                                            {/* ГИП (отображается под нормоконтролем, если есть) */}
                                                            {latestVersion.signatures.some((s: any) => s.signerType === 'gip') && (
                                                                <div className={styles.legalSection}>
                                                                    <div className={styles.legalHeader}>ГИП</div>
                                                                    {latestVersion.signatures
                                                                        .filter((s: any) => s.signerType === 'gip')
                                                                        .map((s: any) => (
                                                                            <div key={`gip-${s.employerId}`} className={styles.signatureRow}>
                                                                                <span className={styles.signerName} title={s.employerName}>
                                                                                    {s.employerName}
                                                                                </span>
                                                                                <SignatureButton
                                                                                    signature={s}
                                                                                    latestVersion={latestVersion}
                                                                                    work={work}
                                                                                    orderId={orderId}
                                                                                    orderName={orderName}
                                                                                    canEdit={canEdit}
                                                                                    isOutsourcer={!!outsourcers.find((o) => o.id === s.employerId)}
                                                                                    user={user}
                                                                                    gipId={this.props.gipId}
                                                                                    onOpenSignDialog={(signatureData, signerId, signerName) => {
                                                                                        this.setState({
                                                                                            showSignDialog: true,
                                                                                            selectedSignature: signatureData,
                                                                                            selectedSignerId: signerId,
                                                                                            selectedSignerName: signerName
                                                                                        })
                                                                                    }} onOpenAttachDialog={(signatureData, signerId, signerName) => {
                                                                                        this.setState({
                                                                                            showAttachDialog: true,
                                                                                            attachSignature: signatureData,
                                                                                            attachSignerId: signerId,
                                                                                            attachSignerName: signerName
                                                                                        })
                                                                                    }}
                                                                                    getSignerType={getSignerType}
                                                                                    getSignerTypeDisplay={getSignerTypeDisplay}
                                                                                />
                                                                            </div>
                                                                        ))}
                                                                </div>
                                                            )}
                                                            {/* Юр.лицо (отображается под нормоконтролем, если есть) */}
                                                            {latestVersion.signatures.some((s: any) => s.signerType === 'legal') && (
                                                                <div className={styles.legalSection}>
                                                                    <div className={styles.legalHeader}>Юр.лицо</div>
                                                                    {latestVersion.signatures
                                                                        .filter((s: any) => s.signerType === 'legal')
                                                                        .map((s: any) => (
                                                                            <div key={`legal-${s.employerId}`} className={styles.signatureRow}>
                                                                                <span className={styles.signerName} title={s.employerName}>
                                                                                    {s.employerName}
                                                                                </span>
                                                                                <SignatureButton
                                                                                    signature={s}
                                                                                    latestVersion={latestVersion}
                                                                                    work={work}
                                                                                    orderId={orderId}
                                                                                    orderName={orderName}
                                                                                    canEdit={canEdit}
                                                                                    isOutsourcer={!!outsourcers.find((o) => o.id === s.employerId)}
                                                                                    user={user}
                                                                                    gipId={this.props.gipId}
                                                                                    onOpenSignDialog={(signatureData, signerId, signerName) => {
                                                                                        this.setState({
                                                                                            showSignDialog: true,
                                                                                            selectedSignature: signatureData,
                                                                                            selectedSignerId: signerId,
                                                                                            selectedSignerName: signerName
                                                                                        })
                                                                                    }} onOpenAttachDialog={(signatureData, signerId, signerName) => {
                                                                                        this.setState({
                                                                                            showAttachDialog: true,
                                                                                            attachSignature: signatureData,
                                                                                            attachSignerId: signerId,
                                                                                            attachSignerName: signerName
                                                                                        })
                                                                                    }}
                                                                                    getSignerType={getSignerType}
                                                                                    getSignerTypeDisplay={getSignerTypeDisplay}
                                                                                />
                                                                            </div>
                                                                        ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        ) : (
                            <div className={styles.noBlockSelected}>
                                Добавьте задание или загрузите файлы результата работ
                            </div>
                        )}
                    </div>

                    {/* Комментарии */}
                    {comments.length > 0 && (
                        <div className={styles.commentsSection}>
                            <h3 className={styles.sectionTitle}>Замечания заказчика</h3>
                            <div className={styles.rowTaskRight}>
                                <div className={styles.rowTaskLeftItemHeader} style={{ marginLeft: "8px" }}>
                                    <div className={styles.version}>Версия</div>
                                    <div className={styles.commentRemark}>Комментарий</div>
                                    <div className={styles.commentRemarkFile}>Файлы замечаний</div>
                                </div>
                                <div
                                    className={styles.rowTaskLeftItem}
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        paddingBottom: "10px",
                                    }}
                                >
                                    {comments.map((comment, i) => (
                                        <div key={i} className={styles.commentItem}>
                                            <div className={styles.version}>
                                                Версия_{comment.version}
                                            </div>
                                            <div
                                                className={styles.commentRemark}
                                                style={{ width: "380px", marginRight: "20px" }}
                                            >
                                                {comment.comment}
                                            </div>
                                            <div className={styles.commentRemarkFile}>
                                                {comment.commentFiles?.map((commentFile, index) => (
                                                    <FileLink<any>
                                                        key={index}
                                                        baseUrl={baseUrl}
                                                        file={{
                                                            fileId: commentFile.fileId,
                                                            fileName: commentFile.fileName,
                                                        }}
                                                        style={{
                                                            marginBottom: "8px",
                                                            minWidth: "200px",
                                                            width: "200px",
                                                            fontSize: "12px",
                                                        }}
                                                        readOnly={true}
                                                        onRemove={eat}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )
    }
}

export const RowTask = observer(Row)

interface VersionDetailsProps {
    fileVersions: any[]
    baseUrl: string
    downloadVersion: (guid: string, fileId: string, version: number) => Promise<any>
    removeVersion: (guid: string, fileId: string, version: number) => void
    canDownload: boolean
    canDelete: boolean
    taskGuid: string
    onNotifySuccess: (error: string) => void
}

const VersionDetails = ({
    fileVersions,
    baseUrl,
    downloadVersion,
    removeVersion,
    canDownload,
    canDelete,
    taskGuid, onNotifySuccess
}: VersionDetailsProps) => {
    const [showDelete, setshowDelete] = useState(false)
    const [selectedVersion, setSelectedVersion] = useState<any>(null)

    const handleDeleteClick = (version: any) => {
        setSelectedVersion(version)
        setshowDelete(true)
    }

    return (
        <div className={styles.popupVersions}>
            <Dialog
                title={`Удаление версии`}
                isOpen={showDelete}
                onClose={() => setshowDelete(false)}
                backdropClassName="standard"
                className={`${styles.FilesDialog} figma-dialog`}
            >
                <div className={`${Classes.DIALOG_BODY} `}>
                    <div className={styles.showDialogTask}>
                        {selectedVersion && `Вы действительно хотите удалить файл "${selectedVersion.name}" (версия ${selectedVersion.version})?`}
                    </div>
                </div>
                <div className={Classes.DIALOG_FOOTER}>
                    <Buttons
                        left={
                            <div style={{ display: "flex" }}>
                                <PlanrButton
                                    size="small"
                                    type="danger"
                                    onClick={() => {
                                        if (selectedVersion) {
                                            removeVersion(taskGuid, selectedVersion.id, selectedVersion.version)
                                            setshowDelete(false)
                                        }
                                    }}
                                >
                                    Удалить
                                </PlanrButton>
                                <PlanrButton
                                    size="small"
                                    type="graybtn"
                                    onClick={() => setshowDelete(false)}
                                >
                                    Отмена
                                </PlanrButton>
                            </div>
                        }
                    />
                </div>
            </Dialog>

            <div className={styles.versionDetailsContainer}>
                {/* Заголовки как в основной таблице - теперь с колонкой Тип */}
                <div className={styles.filesTableHeader}>
                    <div className={styles.fileTypeCol}>Тип</div>
                    <div className={styles.fileNameCol}>Файл</div>
                    <div className={styles.fileVersionCol} style={{ color: "#00273d", cursor: "default" }}>Версия</div>
                    <div className={styles.fileDateCol}>Дата загрузки</div>
                    <div className={styles.fileActionsCol}></div>
                </div>

                {fileVersions.map((file, index) => {
                    // Определяем тип файла
                    const fileType = file.type === Constants.orderFileTypeContentTaskResult ? 'result' : 'source'

                    return (
                        <div key={file.id} className={styles.fileVersionGroup}>
                            <div className={styles.fileRow}>
                                <div className={styles.fileTypeCol}>
                                    <span className={fileType === 'result' ? styles.resultBadge : styles.sourceBadge}>
                                        {fileType === 'result' ? 'Результат' : 'Исходный'}
                                    </span>
                                </div>
                                <div className={styles.fileNameCol}>
                                    <FileLink
                                        baseUrl={baseUrl}
                                        file={{
                                            fileId: file.id,
                                            fileName: file.name,
                                        }}
                                        style={{
                                            whiteSpace: "normal",
                                            fontSize: " 10px",
                                            height: "auto",
                                            padding: "8px 12px",
                                            borderColor: "#ea561e",
                                            color: "#ea561e"
                                        }}
                                        readOnly={true}
                                        onRemove={eat}
                                    />
                                </div>
                                <div className={styles.fileVersionCol}>
                                    <span className={styles.versionLink}>
                                        Версия_{file.version}
                                    </span>
                                </div>
                                <div className={styles.fileDateCol}>
                                    {file.created || '-'}
                                    <br />
                                    <span className={styles.time}>{file.time || ''}</span>
                                </div>
                                <div className={styles.fileActionsCol} style={{ display: 'flex', gap: '8px' }}>
                                    {(canDownload || file.canDeleteVersion) && (
                                        <DownloadButton
                                            loading={false}
                                            onClick={async () => {
                                                await downloadVersion(taskGuid, file.id, file.version)
                                            }}
                                        />
                                    )}

                                    {(canDelete || file.canDeleteVersion) && (
                                        <PlanrButton
                                            type="lightdanger"
                                            size="small"
                                            icon="general-trash"
                                            title="Удалить версию"
                                            onClick={() => handleDeleteClick(file)}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className={styles.filePathContainer}>
                                <FilePathEditor
                                    filePath={file.path}
                                    fileId={file.id}
                                    onSave={async (fileId, path) => {
                                    }}
                                    readOnly={true}
                                    onCopy={() => {
                                        onNotifySuccess?.('Путь скопирован в буфер обмена')
                                    }}
                                />
                            </div>
                            {/* Если это файл результата, показываем подписи для этой версии */}
                            {fileType === 'result' && file.signatures?.length > 0 && (
                                <div className={styles.signaturesContainer}>
                                    <div className={styles.signaturesThreeColumns}>
                                        {/* Столбец "Разработал" */}
                                        <div className={styles.signatureColumn}>
                                            <div className={styles.columnHeader}>Разработал</div>
                                            {file.signatures
                                                .filter((s: any) => s.signerType === 'worked')
                                                .map((s: any) => (
                                                    <div key={`worked-${s.employerId}`} className={styles.signatureRow}>
                                                        <span className={styles.signerName} title={s.employerName}>
                                                            {s.employerName}
                                                        </span>
                                                        <span className={s.signed ? styles.signedBadge : styles.notSignedBadge}>
                                                            {s.signed ? "Подпись" : "Нет"}
                                                        </span>
                                                    </div>
                                                ))}
                                            {file.signatures.filter((s: any) => s.signerType === 'worked').length === 0 && (
                                                <div className={styles.emptyColumn}>—</div>
                                            )}
                                        </div>

                                        {/* Столбец "Проверил" */}
                                        <div className={styles.signatureColumn}>
                                            <div className={styles.columnHeader}>Проверил</div>
                                            {file.signatures
                                                .filter((s: any) => s.signerType === 'checked')
                                                .map((s: any) => (
                                                    <div key={`checked-${s.employerId}`} className={styles.signatureRow}>
                                                        <span className={styles.signerName} title={s.employerName}>
                                                            {s.employerName}
                                                        </span>
                                                        <span className={s.signed ? styles.signedBadge : styles.notSignedBadge}>
                                                            {s.signed ? "Подпись" : "Нет"}
                                                        </span>
                                                    </div>
                                                ))}
                                            {file.signatures.filter((s: any) => s.signerType === 'checked').length === 0 && (
                                                <div className={styles.emptyColumn}>—</div>
                                            )}
                                        </div>

                                        {/* Столбец "Нормоконтроль" + Юр.лицо */}
                                        <div className={styles.signatureColumn}>
                                            <div className={styles.columnHeader}>Нормоконтроль</div>
                                            {file.signatures
                                                .filter((s: any) => s.signerType === 'control')
                                                .map((s: any) => (
                                                    <div key={`control-${s.employerId}`} className={styles.signatureRow}>
                                                        <span className={styles.signerName} title={s.employerName}>
                                                            {s.employerName}
                                                        </span>
                                                        <span className={s.signed ? styles.signedBadge : styles.notSignedBadge}>
                                                            {s.signed ? "Подпись" : "Нет"}
                                                        </span>
                                                    </div>
                                                ))}
                                            {file.signatures.filter((s: any) => s.signerType === 'control').length === 0 && (
                                                <div className={styles.emptyColumn}>—</div>
                                            )}
                                            {/* ГИП (отображается под нормоконтролем, если есть) */}
                                            {file.signatures.some((s: any) => s.signerType === 'gip') && (
                                                <div className={styles.legalSection}>
                                                    <div className={styles.legalHeader}>ГИП</div>
                                                    {file.signatures
                                                        .filter((s: any) => s.signerType === 'gip')
                                                        .map((s: any) => (
                                                            <div key={`gip-${s.employerId}`} className={styles.signatureRow}>
                                                                <span className={styles.signerName} title={s.employerName}>
                                                                    {s.employerName}
                                                                </span>
                                                                <span className={s.signed ? styles.signedBadge : styles.notSignedBadge}>
                                                                    {s.signed ? "Подпись" : "Нет"}
                                                                </span>
                                                            </div>
                                                        ))}
                                                </div>
                                            )}
                                            {/* Юр.лицо (отображается под нормоконтролем, если есть) */}
                                            {file.signatures.some((s: any) => s.signerType === 'legal') && (
                                                <div className={styles.legalSection}>
                                                    <div className={styles.legalHeader}>Юр.лицо</div>
                                                    {file.signatures
                                                        .filter((s: any) => s.signerType === 'legal')
                                                        .map((s: any) => (
                                                            <div key={`legal-${s.employerId}`} className={styles.signatureRow}>
                                                                <span className={styles.signerName} title={s.employerName}>
                                                                    {s.employerName}
                                                                </span>
                                                                <span className={s.signed ? styles.signedBadge : styles.notSignedBadge}>
                                                                    {s.signed ? "Подпись" : "Нет"}
                                                                </span>
                                                            </div>
                                                        ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export const DownloadButton = (props: { loading: boolean; onClick: () => void; gray?: boolean, title?: string, round?: boolean }) => {
    return (
        <PlanrButton
            type={props.gray ? "graybtn" : "lightgreenish"}
            size={!props.gray && !props.round ? "small" : "medium"}
            icon={props.loading ? undefined : "general-download"}
            onClick={props.onClick}
            disabled={props.loading}
            round={props.gray || props.round ? true : false}
            title={props.title || "Скачать архив с файлами по заданию"}
        >
            {props.loading && <Spinner intent="primary" size={16} />}
        </PlanrButton>
    )
}


interface SignatureButtonProps {
    signature: any // Подпись участника
    latestVersion: any // Версия файла
    work: any // Работа
    orderId: string
    orderName: string
    canEdit: boolean
    user: string  // Текущий пользователь
    gipId?: string | null  // ID ГИПа
    isOutsourcer?: boolean
    onOpenSignDialog: (signatureData: PendingSignatureItemType, signerId: string, signerName: string) => void
    onOpenAttachDialog?: (signatureData: PendingSignatureItemType, signerId: string, signerName: string) => void
    getSignerType: (type: string) => "worked" | "checked" | "control" | "legal" | "gip" | "leader"
    getSignerTypeDisplay: (type: string) => string
}

const SignatureButton: React.FC<SignatureButtonProps> = ({
    signature,
    latestVersion,
    work,
    orderId,
    orderName,
    canEdit,
    user,
    gipId, isOutsourcer,
    onOpenSignDialog,
    onOpenAttachDialog,
    getSignerType,
    getSignerTypeDisplay,
}) => {
    // Проверяем, может ли пользователь подписать
    const canSign = () => {
        if (signature.signed) return false

        if (gipId && signature.employerId !== gipId) return user === gipId
        return false
    }

    const handleSign = () => {
        const signatureData: PendingSignatureItemType = {
            signatureId: signature.signatureId,
            orderId: orderId,
            orderNumber: work.partNumber,
            orderName: orderName,
            contentGuid: work.guid,
            contentName: work.name,
            version: latestVersion.version,
            signerType: getSignerType(signature.signerType),
            signerTypeDisplay: getSignerTypeDisplay(signature.signerType),
            fileName: latestVersion.name,
            fileId: latestVersion.id,
            createdAt: latestVersion.date || "",
            date: latestVersion.date || "",
            existingSignature: "",
            signatureSource: "task"

        }

        // Окно подписания универсальное: и сертификатом, и файлом подписи —
        // для аутсорсера оно просто открывается в режиме «Файлом подписи»
        onOpenSignDialog(signatureData, signature.employerId, signature.employerName)
    }

    // Если уже подписано - показываем "Подпись"
    if (signature.signed) {
        return (
            <span className={styles.signedBadge}>
                Подписан
            </span>
        )
    }

    // Если не подписано и можно подписать - показываем кнопку
    if (canSign()) {
        return (
            <PlanrButton
                type='primary'
                size="small"
                onClick={handleSign}
                style={{
                    minWidth: '60px',
                    height: '24px',
                    padding: '0 8px',
                    fontSize: '11px',
                    fontWeight: '600',
                    borderRadius: '12px',
                    lineHeight: '1'
                }}
            >
                {isOutsourcer ? 'Прикрепить' : 'Подписать'}
            </PlanrButton>
        )
    }

    // Если не подписано и нельзя подписать - показываем "Нет"
    return (
        <span className={styles.notSignedBadge}>
            Нет
        </span>
    )
}



interface FilePathEditorProps {
    filePath: string | null
    fileId: string
    onSave: (fileId: string, path: string) => Promise<void>
    onCopy: () => void
    readOnly?: boolean
}

const FilePathEditor: React.FC<FilePathEditorProps> = ({
    filePath,
    fileId,
    onSave, onCopy,
    readOnly = false
}) => {
    const [isEditing, setIsEditing] = useState(false)
    const [pathValue, setPathValue] = useState(filePath || '')
    const [isSaving, setIsSaving] = useState(false)

    const handleCopy = async () => {
        if (filePath) {
            try {
                await navigator.clipboard.writeText(filePath)
                onCopy()

            } catch (e) {
                // Fallback для старых браузеров
                const textarea = document.createElement('textarea')
                textarea.value = filePath
                document.body.appendChild(textarea)
                textarea.select()
                document.execCommand('copy')
                document.body.removeChild(textarea)
                onCopy()

            }
        }
    }

    const handleSave = async () => {
        if (!pathValue.trim()) {
            return
        }
        setIsSaving(true)
        try {
            await onSave(fileId, pathValue)
            setIsEditing(false)
        } finally {
            setIsSaving(false)
        }
    }

    const handleCancel = () => {
        setPathValue(filePath || '')
        setIsEditing(false)
    }

    if (readOnly) {
        return (
            <div className={styles.filePathRow}>
                <span className={styles.filePathLabel}>Путь к файлу:</span>
                {filePath ? (
                    <>
                        <span
                            className={styles.filePathLink}
                            onClick={(e) => {
                                e.stopPropagation()
                                handleCopy()
                            }}
                        >
                            {filePath}
                        </span>

                    </>
                ) : (
                    <span className={styles.noFilePath}>Не указан</span>
                )}
            </div>
        )
    }

    if (isEditing) {
        return (
            <div className={styles.filePathEditRow}>
                <span className={styles.filePathLabel}>Путь к файлу:</span>
                <div className={styles.filePathEditContent}>
                    <div className={`${Classes.INPUT_GROUP} planr-default-input`}>
                        <TextArea
                            value={pathValue}
                            onChange={(e: any) => setPathValue(e.target.value)}
                            className={Classes.FILL}
                            placeholder="Введите путь к файлу..."
                            autoResize={true}
                        />
                    </div>
                    <div className={styles.filePathEditButtons}>
                        <PlanrButton
                            type="greenish"
                            size="small"
                            icon="general-check"
                            onClick={handleSave}
                            loading={isSaving}
                            title="Сохранить"
                        />
                        <PlanrButton
                            type="secondary"
                            size="small"
                            icon="general-cross-small"
                            onClick={handleCancel}
                            title="Отмена"
                        />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.filePathRow}>
            <span className={styles.filePathLabel}>Путь к файлу:</span>
            {filePath ? (
                <>
                    <span
                        className={styles.filePathLink}
                        onClick={(e) => {
                            e.stopPropagation()
                            handleCopy()
                        }}
                    >
                        {filePath}
                    </span>

                    <PlanrButton
                        type="lightblueish"
                        size="small"
                        icon="general-edit"
                        onClick={() => setIsEditing(true)}
                        title="Редактировать путь"
                        style={{ flexShrink: 0 }}
                    />
                </>
            ) : (
                <>
                    <span className={styles.noFilePath}>Не указан</span>
                    <PlanrButton
                        type="lightblueish"
                        size="small"
                        icon="general-edit"
                        onClick={() => setIsEditing(true)}
                        title="Добавить путь"
                        style={{ flexShrink: 0 }}
                    />
                </>
            )}
        </div>
    )
}


export default RowTask